import { AuthOption, AuthOptionType, OAuth2Auth, SecretsAuth } from "../../../config/types";
import ErrorScreen from "../error-remediation/ErrorScreen";
import ConnectorSetupOAuth from "./ConnectorSetupOAuth";
import ConnectorSetupStepSecrets from "./ConnectorSetupStepSecrets";
import useIntegrationSetupContext from "./useIntegrationSetupContext";
import { areAllSecretsFilled, areRequiredSecretsFilled } from "./utils/secretHelpers";
import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";
import { initiateOAuth } from "../../../utils/fetchUtils";
import { useState } from "react";

type Props = {
  authOption: AuthOption;
};

const ConnectorSetupStepSwitcher = ({ authOption }: Props) => {
  // hooks
  const { onExit, getFormDataProperty, apiBaseURL, registeredUser, connector, registeredUserSecrets } =
    useIntegrationSetupContext();
  const { linkToken } = useAgentHandlerLinkModalContext();
  // Set on the first render, not in an effect: otherwise ConnectorSetupOAuth mounts
  // first when only optional secrets are blank and starts OAuth before the form shows.
  const [isUsingSecrets, setIsUsingSecrets] = useState(
    () =>
      authOption.type === AuthOptionType.OAUTH2 &&
      !areAllSecretsFilled(
        (authOption as OAuth2Auth).registered_user_secrets ?? [],
        getFormDataProperty
      )
  );
  const [secretsErrorMessage, setSecretsErrorMessage] = useState<string | undefined>(undefined);
  const [prefetchedOAuthData, setPrefetchedOAuthData] = useState<
    { authorizeURL: string; key: string } | undefined
  >(undefined);

    // Handler for when secrets form is submitted
  const handleSecretsSubmit = (navigateToNextStep: () => void) => {
    if (authOption.type === AuthOptionType.OAUTH2) {
      const oauth2Auth = authOption as OAuth2Auth;
      if (areRequiredSecretsFilled(oauth2Auth.registered_user_secrets, getFormDataProperty)) {
        // Pre-validate via /oauth/init/. The BE enforces blocked_regex there;
        setSecretsErrorMessage(undefined);
        initiateOAuth({
          apiBaseURL,
          registeredUserID: registeredUser.id,
          connectorID: connector.id,
          authOptionName: oauth2Auth.name,
          linkToken,
          registeredUserSecrets,
          onResponse: (response) => {
            setPrefetchedOAuthData({
              authorizeURL: response.oauth_authorize_url,
              key: response.oauth_key,
            });
            setIsUsingSecrets(false);
          },
          onError: (errorMessage?: string) => {
            setSecretsErrorMessage(
              errorMessage ?? "Something went wrong. Please try again."
            );
          },
        });
      } else {
        // Stay on the form: navigating would post the linking step without OAuth
        // and report success with no credential.
        setSecretsErrorMessage("Please fill in all required fields.");
      }
    } else {
      // For non-OAuth types, just use the default navigation
      navigateToNextStep();
    }
  };

  switch (authOption.type) {
    case AuthOptionType.SECRETS:
      return <ConnectorSetupStepSecrets secretsAuth={authOption as SecretsAuth} />;
    case AuthOptionType.OAUTH2:
      const oauth2Auth = authOption as OAuth2Auth;

      // Check if there are registered_user_secrets that need to be filled out
      if (oauth2Auth.registered_user_secrets && oauth2Auth.registered_user_secrets.length > 0) {
        // If any required secrets aren't filled out, show the secrets form first
        if (
          !areRequiredSecretsFilled(oauth2Auth.registered_user_secrets, getFormDataProperty) ||
          isUsingSecrets
        ) {
          return (
            <ConnectorSetupStepSecrets
              secretsAuth={oauth2Auth as OAuth2Auth}
              onSubmit={handleSecretsSubmit}
              errorMessage={secretsErrorMessage}
            />
          );
        }
      }

      return <ConnectorSetupOAuth oauth2Auth={oauth2Auth} initialOAuthData={prefetchedOAuthData} />;
    case AuthOptionType.NONE:
    default:
      return <ErrorScreen onClickBottomAction={() => {}} onExit={onExit} />;
  }
};

export default ConnectorSetupStepSwitcher;